import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

import { useInvoiceForm, MODE } from "./useInvoiceForm";
import { InvoiceFormActions } from "./InvoiceFormActions";
import { MainInfoSection } from "./sections/MainInfoSection";
import { CounterpartySection } from "./sections/CounterpartySection";
import { RecipientSection } from "./sections/RecipientSection";
import { PaymentSection } from "./sections/PaymentSection";
import { CommentsSection } from "./sections/CommentsSection";
import { PositionsSection } from "./sections/PositionsSection";
import { SummarySection } from "./sections/SummarySection";
import { CorrectionViewDetailsSection } from "./sections/CorrectionViewDetailsSection";
import { CorrectionViewPositionsSection } from "./sections/CorrectionViewPositionsSection";
import { RelatedCorrectionsSection } from "./sections/RelatedCorrectionsSection";
import { getCorrectionDetails } from "@/service/weexpertService";
import { INVOICE_TYPES, CORRECTABLE_INVOICE_TYPES } from "@/constants";

export { MODE } from "./useInvoiceForm";

export const InvoiceForm = ({ invoice, mode, onSave }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inputChecked, setInputChecked] = useState(false);

  const form = useInvoiceForm(invoice, mode);

  const isCorrection = invoice?.invoiceType === INVOICE_TYPES.FAKTURA_KORYGUJACA;
  const isCorrectable = invoice && CORRECTABLE_INVOICE_TYPES.includes(invoice.invoiceType);

  const { data: correctionDetailsData } = useQuery({
    queryKey: ['correctionDetails', invoice?.id],
    queryFn: () => getCorrectionDetails(invoice.id),
    enabled: mode === MODE.VIEW && isCorrection && !!invoice?.id,
  });

  const correctionDetails = correctionDetailsData?.data?.correctionDetails;

  const mutation = useMutation({
    mutationFn: () => {
      setInputChecked(true);
      const errors = form.validate();
      if (errors.length > 0) {
        return Promise.resolve();
      }
      const submitData = form.getSubmitData();
      return onSave(submitData);
    },
  });

  const handleSave = () => {
    if (onSave) {
      mutation.mutate();
    }
  };

  if (form.isLoading) {
    return <Loader />;
  }

  // View mode layout
  if (mode === MODE.VIEW) {
    return (
      <div className="w-full bg-grayLightMainBg">
        <InvoiceFormActions form={form} isCorrection={isCorrection} isCorrectable={isCorrectable} />

        <div className='w-full p-8'>
          <MainInfoSection form={form} />

          {/* Correction invoice details section */}
          {isCorrection && correctionDetails && (
            <CorrectionViewDetailsSection correctionDetails={correctionDetails} />
          )}

          <div className='w-full md:w-9/12'>
            <CounterpartySection form={form} />
            <RecipientSection form={form} />
            <PaymentSection form={form} />
            <CommentsSection form={form} />
          </div>

          {/* Created based on section */}
          {invoice && invoice.fromInvoices && invoice.fromInvoices.length > 0 && (
            <div className='py-10'>
              <Typography sx={{ fontSize: '20px', fontWeight: '600', padding: '20px 0px 0px 0px' }}>
                {t("invoices:created_based_on")}
              </Typography>
              {invoice.fromInvoices.map((e) => (
                <div key={e.fromInvoice?.id || e.id} className='pt-3'>
                  <Button
                    className="text-black bg-inherit hover:bg-inherit border-[0px] border-blueBorder shadow-none p-0 opacity-60 hover:opacity-100"
                    onClick={() => navigate(`/admin/invoices/details/${e.fromInvoice.id}`)}
                  >
                    {e.fromInvoice.invoiceId}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Correction positions (before/after/diff) for correction invoices */}
          {isCorrection && invoice?.correctionItems && (
            <CorrectionViewPositionsSection correctionItems={invoice.correctionItems} />
          )}

          {/* Regular positions section */}
          {!isCorrection && (
            <>
              <PositionsSection form={form} inputChecked={inputChecked} />
              <SummarySection form={form} />
            </>
          )}

          {/* Related corrections for correctable invoices */}
          {isCorrectable && (
            <RelatedCorrectionsSection invoiceId={invoice.id} />
          )}

          <ScrollToTop />
        </div>
      </div>
    );
  }

  // Create/Edit mode layout
  return (
    <>
      <InvoiceFormActions form={form} onSave={handleSave} isPending={mutation.isPending} />

      <div className="w-full pb-6 pt-6">
        <div className='w-full md:w-9/12'>
          <MainInfoSection form={form} inputChecked={inputChecked} />
          <CounterpartySection form={form} inputChecked={inputChecked} />
          <RecipientSection form={form} inputChecked={inputChecked} />
          <PaymentSection form={form} inputChecked={inputChecked} />
          <CommentsSection form={form} />
        </div>

        <div className="w-full">
          <PositionsSection form={form} inputChecked={inputChecked} />
          <SummarySection form={form} />
        </div>
      </div>
    </>
  );
};
